import type { LinkRegistry, Pathway } from "../types";

import { links } from "../../linksRegistry";


export const utiPathway: Pathway = {
  id: "uti",
  name: "Uncomplicated UTI",
  population: "Women aged 16–64",
  pgdRefs: ["pgd-1a-nitrofurantoin-uti"],
  links: links,
  clinicalNoteLinkRefs: ["nice-cks-uti-lower", "nhs-england-pharmacy-first-clinical-pathways", "pgd-uti-nitrofurantoin"],

  demographicConfiguration: {
    bannerFields: [
      {
        field: "isFemale",
        label: "Patient sex",
        type: "select",
        selectOptions: [
          { value: "true", label: "Female" },
          { value: "false", label: "Male" },
        ],
      },
      {
        field: "dateOfBirth",
        label: "Date of birth",
        type: "date",
      },
      {
        field: "knownAllergies",
        label: "Known allergies",
        type: "string",
        helpText: "Confirm against current consultation",
      },
    ],
  },

  demographicEligibility: {
    requiresSex: "female",
    minAge: 16,
    maxAge: 65,
    blockMessage: "Uncomplicated UTI is only available for women aged 16 to under 65.",
  },

  entryNode: "q1_sepsis",

  nodes: [
    {
      id: "q1_sepsis",
      type: "yes_no",
      text: "Signs of sepsis or life-threatening emergency?",
      hint: "Extreme confusion, mottled skin, extreme pain, no urination, extreme temperature",
      showSepsisWarning: true,
      yes: "GATEWAY_SEPSIS_EMERGENCY",
      no: "q2_pyelo",
    },

    {
      id: "GATEWAY_SEPSIS_EMERGENCY",
      type: "gateway_alarm",
      heading: "EMERGENCY — Call 999",
      body: "Signs of sepsis or life-threatening emergency detected.",
      action: "close",
      triggersPayment: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    {
      id: "q2_pyelo",
      type: "checklist",
      text: "Any signs of pyelonephritis?",
      items: [
        { id: "loin-pain", text: "Loin pain or tenderness under ribs" },
        { id: "flu-like", text: "Flu-like illness or new muscle aches" },
        { id: "rigors", text: "Shaking chills or rigors" },
        { id: "temp-high", text: "Temperature ≥37.9°C" },
        { id: "nausea", text: "Nausea or vomiting" },
        { id: "fever-chills", text: "Fever or chills in past 48 hours" },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_REFER_PYELO" },
      noneSelected: "q3_differential",
      linkRefs: ["nice-cks-pyelonephritis"],
    },

    {
      id: "q3_differential",
      type: "checklist",
      text: "Any of the following present?",
      items: [
        { id: "discharge", text: "Abnormal vaginal discharge" },
        { id: "urethritis", text: "Signs of urethritis" },
        { id: "sti-risk", text: "Sexual history suggesting STI risk" },
        { id: "pregnancy", text: "Signs or symptoms of pregnancy" },
        { id: "genitourinary", text: "Genitourinary syndrome of menopause" },
        { id: "immunosuppressed", text: "Patient is immunosuppressed" },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_REFER_DIFFERENTIAL" },
      noneSelected: "GATEWAY_DIFFERENTIAL_CLEARED",
    },

    {
      id: "GATEWAY_DIFFERENTIAL_CLEARED",
      type: "gateway_progression",
      heading: "Differential screen cleared",
      body: "No alternative diagnoses identified. Proceeding to symptom assessment.",
      triggersPayment: true,
      next: "q4_symptoms",
    },

    {
      id: "q4_symptoms",
      type: "multi_select",
      text: "Select all symptoms present",
      groups: [
        {
          id: "key-symptoms",
          label: "Key symptoms (diagnostic)",
          items: [
            { id: "dysuria", text: "Dysuria" },
            { id: "nocturia", text: "New nocturia" },
            { id: "cloudy", text: "Urine visibly cloudy" },
          ],
          isKeyDiagnostic: true,
        },
        {
          id: "supporting-symptoms",
          label: "Supporting symptoms",
          items: [
            { id: "urgency", text: "Urgency" },
            { id: "frequency", text: "Frequency" },
            { id: "haematuria", text: "Visible haematuria" },
            { id: "suprapubic", text: "Suprapubic pain" },
          ],
        },
      ],
      routing: (selectedIds: string[]) => {
        // Pharmacy First UTI symptom rule:
        //   0 key + 0 supporting → self-care + pain relief
        //   0 key + ≥1 supporting → UTI equally likely as other diagnoses
        //                            → onward referral
        //   exactly 1 key → onward referral (insufficient diagnostic certainty)
        //   ≥2 key → continue to shared decision making (drug selection)
        const keyDiagnosticIds = ["dysuria", "nocturia", "cloudy"];
        const numKey = selectedIds.filter((id) =>
          keyDiagnosticIds.includes(id),
        ).length;
        const numSupporting = selectedIds.length - numKey;

        if (numKey === 0 && numSupporting === 0) {
          return { outcomeId: "OUT_SELF_CARE_NO_SYMPTOMS" };
        }
        if (numKey < 2) {
          return { outcomeId: "OUT_REFER_UTI_UNCERTAIN" };
        }
        return "drug_select";
      },
    },

    {
      id: "drug_select",
      type: "drug_selection",
      rules: [{ condition: "always", pgdId: "pgd-1a-nitrofurantoin-uti" }],
      next: "pgd_exclusions",
    },

    {
      id: "pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: "q5_severity",
    },

    {
      id: "q5_severity",
      type: "severity",
      text: "Overall symptom severity?",
      mild: { outcomeId: "OUT_SELF_CARE_FIRST_LINE" },
      moderate: { outcomeId: "OUT_SUPPLY" },
      severe: { outcomeId: "OUT_SUPPLY" },
    },
  ],

  outcomes: {
    OUT_SUPPLY: {
      id: "OUT_SUPPLY",
      type: "outcome",
      outcomeType: "supply",
      title: "Supply Nitrofurantoin",
      safetyNetting: [
        "Return if no improvement within 48 hours",
        "Seek urgent help if symptoms worsen",
        "999 or A&E if signs of sepsis",
      ],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_SELF_CARE_NO_SYMPTOMS: {
      id: "OUT_SELF_CARE_NO_SYMPTOMS",
      type: "outcome",
      outcomeType: "self_care",
      title: "Self-care and pain relief",
      reason:
        "No diagnostic or supporting symptoms reported — UTI not indicated.",
      safetyNetting: [
        "Advise hydration and over-the-counter pain relief if needed",
        "Return for reassessment if urinary symptoms develop within 48 hours",
        "Refer to GP if any new or worsening symptoms",
      ],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_REFER_UTI_UNCERTAIN: {
      id: "OUT_REFER_UTI_UNCERTAIN",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Onward referral — UTI equally likely as other diagnoses",
      reason:
        "Diagnostic certainty for UTI not met (fewer than 2 key diagnostic symptoms). Refer to GP, sexual health clinic, or other appropriate provider for assessment.",
      safetyNetting: [
        "Refer to GP, SHU clinic, or other appropriate provider",
        "Advise self-care and hydration in the interim",
        "Return urgently if symptoms worsen or signs of pyelonephritis develop",
      ],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_SELF_CARE_FIRST_LINE: {
      id: "OUT_SELF_CARE_FIRST_LINE",
      type: "outcome",
      outcomeType: "self_care",
      title: "Self-care first line — mild symptoms",
      reason: "Return within 48 hours if no improvement for reassessment",
      safetyNetting: ["Return if symptoms worsen"],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_REFER_PYELO: {
      id: "OUT_REFER_PYELO",
      type: "outcome",
      outcomeType: "refer_urgent",
      title: "Urgent same-day referral — pyelonephritis",
      safetyNetting: [
        "Arrange urgent GP review or out-of-hours assessment today",
        "If symptoms worsen before review, call 999",
      ],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_REFER_DIFFERENTIAL: {
      id: "OUT_REFER_DIFFERENTIAL",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Onward referral",
      safetyNetting: ["GP review recommended within 48 hours"],
      referralOptions: ["GP Practice", "Sexual Health Clinic", "Other provider as appropriate"],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_EMERGENCY: {
      id: "OUT_EMERGENCY",
      type: "outcome",
      outcomeType: "emergency",
      title: "EMERGENCY — Call 999",
      reason: "Signs of sepsis or life-threatening emergency",
      safetyNetting: ["Calculate NEWS2. Direct to A&E immediately."],
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },

    OUT_CANNOT_SUPPLY: {
      id: "OUT_CANNOT_SUPPLY",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply under this PGD — exclusion met",
      reason: "Refer to prescriber",
      safetyNetting: ["Patient requires GP or specialist review"],
      leafletRefs: ["leaflet-uti-en", "leaflet-uti-cy", "leaflet-uti-other-languages"],
    },
  },
};
